import Phaser from 'phaser';
import type { TowerState } from '../core/tower';
import type { TowerType } from '../core/types';
import type { Vec2 } from '../core/vec';
import { BALANCE } from '../config/balance';
import { SPRITE_FACING } from './theme';

/**
 * TowerView — thin wrapper binding a Phaser sprite to a single TowerState
 * by id. Retextures automatically when the tower is upgraded (tier change).
 *
 * Depth layers: range ring → 2.5, sprite → 3
 */

/** Placed-tower display size (px). Plots are 64px; this fills most of the square. */
/** The outer (thrown-dagger) ring of a Multi-attacker — royal gold. */
const OUTER_RING_COLOR = 0xffd21f;
const TOWER_DISPLAY = 60;

/** Below this much displacement from its plot, a melee unit keeps its facing. */
const FACE_DEADZONE = 2;

/** On-screen px per source-canvas px: a normal 96px sprite renders at 60px. */
const DISPLAY_PER_SRC = TOWER_DISPLAY / 96;
/**
 * Ninja Hambi & Sensei Ketchup ship on a bigger source canvas than the usual 96px so
 * their crossed katanas / straw-hat brim / aura have room to spread out. Each is rendered
 * at its own source dimensions × DISPLAY_PER_SRC, so the central burger still matches the
 * other towers on-screen while the flourishes extend past the 64px plot — and a non-square
 * source (the 110 × 100 Ninja) keeps its aspect instead of being squished into a square.
 */
const LARGE_FOOTPRINT_SRC: Partial<Record<TowerType, { w: number; h: number }>> = {
  ninja: { w: 110, h: 100 }, // → 68.75 × 62.5 on-screen
  sensei: { w: 128, h: 128 }, // → 80 × 80 on-screen
};
interface DisplaySize {
  w: number;
  h: number;
}
const displaySizeFor = (type: TowerType): DisplaySize => {
  const src = LARGE_FOOTPRINT_SRC[type];
  return src
    ? { w: src.w * DISPLAY_PER_SRC, h: src.h * DISPLAY_PER_SRC }
    : { w: TOWER_DISPLAY, h: TOWER_DISPLAY };
};

/**
 * Cooldown bar geometry for the Wise Hambi, Wizard Tower and Phantom Hambi
 * (drawn above the sprite).
 */
const CD_BAR_W = 44;
const CD_BAR_H = 5;
const CD_BAR_OFFSET_Y = -(TOWER_DISPLAY / 2) - 8;
/** Bar fill while counting down (serene blue) and while the freeze is active (bright blue). */
const CD_FILL = 0x8ec9ff;
const CD_FILL_ACTIVE = 0x2e86ff;
const CD_BG = 0x1a2233;
/**
 * The Phantom's bar is RED — its own colour, matching the red bullet of the
 * hitscan special the bar is counting down to, and distinct at a glance from
 * the blue freeze bars the Wise Hambi and Wizard share.
 */
const CD_FILL_PHANTOM = 0xff3b30;
const CD_BG_PHANTOM = 0x2a1010;

/**
 * Structural-HP bar geometry (King Ember's Ember Barrage only). It sits above
 * the cooldown bar so the two never collide on a Wise Hambi or Wizard Tower.
 */
const HP_BAR_W = 44;
const HP_BAR_H = 6;
const HP_BAR_OFFSET_Y = -(TOWER_DISPLAY / 2) - 18;
/** Bar fill — green at every HP level, so damage reads as a shrinking green bar. */
const HP_FILL = 0x3ddc4a;
const HP_BG = 0x14210f;

/**
 * Timed-effect dressing (The Hub): small potion icons in a row above the
 * tower, and the Diamond's floating blue star. Both sit above the HP bar's
 * slot so nothing collides when the barrage is armed.
 */
const FX_ICON_SIZE = 32;
const FX_ICON_GAP = 4;
const FX_ICON_OFFSET_Y = -(TOWER_DISPLAY / 2) - 40;
const SHIELD_STAR_SIZE = 28;
const SHIELD_STAR_OFFSET_Y = -(TOWER_DISPLAY / 2) - 70;

/** What the render layer knows about a tower's running effects this frame. */
export interface TowerFx {
  /** Atlas keys of the potion icons to float above the tower, left to right. */
  icons: readonly string[];
  /** The Diamond is running: draw the blue star. */
  shield: boolean;
}

/** Seconds a destroyed tower takes to fade off its plot. */
const FADE_MS = 600;

/**
 * Bow Hambi's draw (spec Revision 7). Over the last BOW_DRAW_S of his firing
 * clock the sprite leans back and squashes — the string coming back — and the
 * shot snaps it forward with a short overshoot. Read off the sim's cooldown,
 * so it needs no event and stays in step with the arrow that actually leaves.
 */
const BOW_DRAW_S = 0.3;
const BOW_DRAW_SQUASH = 0.14;
const BOW_DRAW_SHIFT = 5;

/** Null-Hex stun visual: grey wash on the sprite + golden sparks orbiting above. */
const STUN_TINT = 0x6f6f80;
/** Veggie-Hex fire-rate-slow tint — a sickly green wash (distinct from stun). */
const SLOW_TINT = 0x8bc34a;
/** A poisoned tower (EnemySpec.towerFire.poison) — brighter than the hex's slow. */
const POISON_TINT = 0x5cff4a;
const STUN_SPARKS = 3;
const STUN_ORBIT_RX = 20;
const STUN_ORBIT_RY = 7;
const STUN_ORBIT_Y = -(TOWER_DISPLAY / 2) - 14;
/** Orbit angular speed (radians per second). */
const STUN_ORBIT_SPEED = 5;

/**
 * Ghosting (Jack'O Ghost, the Blue Ghost; spec 2026-10-07): "the tower turns
 * purple and ghosts rotate above him". The ghosts are the Ghost's own sprite,
 * small and see-through, on the stun sparks' orbit.
 */
const POSSESS_TINT = 0xa45cff;
/**
 * SHROOMNITIZED (the Glowshroom's potion) is the same possession, told apart
 * by its possessor: an orange wash with little glowshrooms circling instead.
 */
const SHROOMNITIZE_TINT = 0xffa040;
const POSSESS_GHOSTS = 3;
const POSSESS_GHOST_SIZE = 22;
const POSSESS_ORBIT_RX = 24;
const POSSESS_ORBIT_RY = 8;
const POSSESS_ORBIT_SPEED = 3.2;

/** Tier-4 Wizard Tower flourish: purple stars orbiting the tower, always on. */
const WIZ_STARS = 4;
const WIZ_ORBIT_RX = 34;
const WIZ_ORBIT_RY = 12;
const WIZ_ORBIT_SPEED = 1.8;
const WIZ_STAR_COLOR = 0xc77dff;

/** Aura glow colors: [outer, inner] per look. */
const AURA_RED: [number, number] = [0xd42a1f, 0xff5a3c]; // ninja/sensei max campaign tier
const AURA_GREEN: [number, number] = [0x1fa83c, 0x3ce06a]; // ninja/sensei Evolution
const AURA_PURPLE: [number, number] = [0x7a1fd4, 0xb45aff]; // patty Evolution

/**
 * Shrekbook Hambi's shout — a permanent black caption above the tower, at every
 * tier, for as long as it stands. It rides above the sprite (and above where the
 * cooldown/HP bars would sit) so nothing overlaps it, and it is drawn with a
 * white stroke because black type on a dark trail would otherwise vanish.
 */
const SHOUT_TEXT: Partial<Record<TowerType, string>> = { shrekbook: 'SHREKBOOK!!!' };
const SHOUT_OFFSET_Y = -(TOWER_DISPLAY / 2) - 12;
/**
 * Where the caption goes once the tower HAS a structural-HP bar. The bar sits
 * at HP_BAR_OFFSET_Y (−48) and the caption is drawn at a higher depth, so at
 * its resting −42 it covered the bar outright — which is why Shrekbook Hambi
 * looked immune to King Ember's barrage. This clears the bar's full height.
 */
const SHOUT_OFFSET_Y_WITH_HP = -(TOWER_DISPLAY / 2) - 26;

/** True per-tier spec lookup (tier4 = the Evolution tier, absent for most towers). */
const tierSpec = (type: TowerType, tier: 1 | 2 | 3 | 4) => {
  const pair = BALANCE.towers[type];
  // Tiers are 1-based; the array is not.
  return [pair.tier1, pair.tier2, pair.tier3, pair.tier4 ?? pair.tier3][tier - 1];
};

export class TowerView {
  readonly id: number;

  private readonly ring: Phaser.GameObjects.Graphics;
  private readonly sprite: Phaser.GameObjects.Sprite;
  private puddle?: Phaser.GameObjects.Image;
  private currentTier: 1 | 2 | 3 | 4;
  private currentType: TowerType;
  /** Second sprite for the tier-4 Ninja's split copy (created when melee2 appears). */
  private cloneSprite?: Phaser.GameObjects.Sprite;
  private cdBg?: Phaser.GameObjects.Rectangle;
  private cdBar?: Phaser.GameObjects.Rectangle;
  /** Idle fill colour of this view's cooldown bar — red on a Phantom, blue otherwise. */
  private cdFill = CD_FILL;
  /** Track colour behind that fill — the Phantom's own dark red, blue-black otherwise. */
  private cdBgFill = CD_BG;
  /** Orbiting golden sparks shown while the tower is stunned (created lazily). */
  private stunGfx?: Phaser.GameObjects.Graphics;
  /** The ghosts circling a possessed tower (EnemySpec.ghosting). */
  private possessGhosts?: Phaser.GameObjects.Image[];
  /** Which possessor `possessGhosts` were drawn for (ghost or Glowshroom). */
  private possessBy?: string;
  /** Orbiting purple stars shown on a max-tier wizard (created lazily). */
  private starGfx?: Phaser.GameObjects.Graphics;
  /** Soft red aura glow under a max-tier Ninja Hambi / Sensei Ketchup (lazy). */
  private auraGfx?: Phaser.GameObjects.Graphics;
  /** Orbiting red stones (+ stars at max tier) around a Sensei Ketchup (lazy). */
  private orbitGfx?: Phaser.GameObjects.Graphics;
  /** Angelic Hambi's orbiting stars (`fx-angelstar`), one image each (lazy). */
  private angelStars: Phaser.GameObjects.Image[] = [];
  /**
   * Structural-HP bar, created the first frame the tower HAS hp — i.e. only
   * once King Ember is on the field. Absent for the rest of the campaign.
   */
  private hpBg?: Phaser.GameObjects.Rectangle;
  private hpBar?: Phaser.GameObjects.Rectangle;
  /** Permanent caption above the tower (Shrekbook Hambi's "SHREKBOOK!!!"). */
  private shout?: Phaser.GameObjects.Text;
  /** Potion icons above the tower, keyed by their atlas key (The Hub). */
  private fxIcons: Phaser.GameObjects.Image[] = [];
  private fxKeys = '';
  /** The Diamond's blue star. */
  private shieldStar?: Phaser.GameObjects.Image;
  private readonly scene: Phaser.Scene;
  /** The sprite's scale at its pinned display size — the bow's draw is relative to it. */
  private baseScale = { x: 1, y: 1 };
  private bowDraw = 0;
  private bowReleasing = false;

  constructor(scene: Phaser.Scene, state: TowerState) {
    this.scene = scene;
    this.id = state.id;
    this.currentTier = state.tier;
    this.currentType = state.type;

    this.ring = scene.add.graphics().setDepth(2.5);
    this.drawRing(state, false);

    const startPos = state.melee ? state.melee.pos : state.pos;
    const display = displaySizeFor(state.type);
    this.sprite = scene.add
      .sprite(startPos.x, startPos.y, `tower-${state.type}-${state.tier}`)
      .setDepth(3)
      .setDisplaySize(display.w, display.h);
    this.baseScale = { x: this.sprite.scaleX, y: this.sprite.scaleY };

    if (state.type === 'cheese') {
      const range = tierSpec('cheese', state.tier).range;
      this.puddle = scene.add
        .image(state.pos.x, state.pos.y, 'decal-cheese')
        .setDepth(1.6)
        .setDisplaySize(range * 2, range * 2)
        .setAlpha(0.55);
    }

    const shout = SHOUT_TEXT[state.type];
    if (shout) {
      this.shout = scene.add
        .text(state.pos.x, state.pos.y + SHOUT_OFFSET_Y, shout, {
          fontSize: '13px',
          color: '#000000',
          fontStyle: 'bold',
          stroke: '#ffffff',
          strokeThickness: 3,
        })
        .setOrigin(0.5, 1)
        .setDepth(3.9);
    }

    // Every tower with a charge worth watching gets the bar. The Phantom's
    // 30-second hitscan special is on the same `specialCooldown` clock the
    // Wizard's is, so the per-frame update below needs no new branch — only
    // the fill colour differs, and it is fixed per view because a tower never
    // changes type.
    //
    // The bar tracks the CURRENT TIER, not just the type: the Phantom's special
    // is tier-3 only, so a tier-1/2 Phantom has no charge to show and gets no
    // bar. `update()` builds it lazily on the upgrade that grants the special.
    if (state.type === 'phantom') {
      this.cdFill = CD_FILL_PHANTOM;
      this.cdBgFill = CD_BG_PHANTOM;
    }
    if (this.wantsCdBar(state)) this.buildCdBar(state);
  }

  /**
   * Does this tower have a charge worth a bar AT ITS CURRENT TIER? The Wise
   * Hambi counts down its freeze ability, the Wizard and the Phantom their
   * timed special — and the Phantom only has one at tier 3.
   */
  private wantsCdBar(state: TowerState): boolean {
    if (state.type === 'wise') return tierSpec('wise', state.tier).abilityCooldown !== undefined;
    if (state.type === 'wizard' || state.type === 'phantom')
      return tierSpec(state.type, state.tier).special !== undefined;
    return false;
  }

  private buildCdBar(state: TowerState): void {
    const bx = state.pos.x - CD_BAR_W / 2;
    const by = state.pos.y + CD_BAR_OFFSET_Y;
    this.cdBg = this.scene.add
      .rectangle(bx, by, CD_BAR_W, CD_BAR_H, this.cdBgFill)
      .setOrigin(0, 0.5)
      .setDepth(3.6);
    this.cdBar = this.scene.add
      .rectangle(bx, by, CD_BAR_W, CD_BAR_H, this.cdFill)
      .setOrigin(0, 0.5)
      .setDepth(3.7);
  }

  private drawRing(state: TowerState, selected: boolean): void {
    if (state.type === 'wise') {
      this.ring.clear();
      return;
    }
    // A Hyno-Fungi's hypnosis shrinks it for a few seconds: show the real reach.
    const shrink =
      (state.rangeShrinkFor ?? 0) > 0 && state.rangeShrinkMult !== undefined
        ? state.rangeShrinkMult
        : 1;
    const range = tierSpec(state.type, state.tier).range * shrink;
    this.ring.clear();
    // Subtle range hint: idle towers show only a faint outline so the circles
    // blend into the scene; the selected tower gets a soft (but clear) ring.
    if (selected) {
      this.ring.fillStyle(0xffffff, 0.03);
      this.ring.fillCircle(state.pos.x, state.pos.y, range);
    }
    this.ring.lineStyle(selected ? 2 : 1, 0xffffff, selected ? 0.55 : 0.12);
    this.ring.strokeCircle(state.pos.x, state.pos.y, range);
    // Samurai Slicer, the Multi-attacker: when he is SELECTED, a second gold
    // ring shows how far his thrown daggers reach — the white inner ring is
    // his slash. Idle, only the inner ring shows, like every other tower.
    const outer = tierSpec(state.type, state.tier).midStrike?.range;
    if (outer && selected) {
      this.ring.fillStyle(OUTER_RING_COLOR, 0.03);
      this.ring.fillCircle(state.pos.x, state.pos.y, outer);
      this.ring.lineStyle(2, OUTER_RING_COLOR, 0.55);
      this.ring.strokeCircle(state.pos.x, state.pos.y, outer);
    }
  }

  /**
   * Bow Hambi's draw, read straight off the sim's firing clock: the string
   * comes back over the last BOW_DRAW_S of the cooldown (the sprite leans back
   * and squashes) and the shot — the cooldown jumping back to full — snaps it
   * forward with a short overshoot. With nothing in range the clock sits at 0
   * and the bow stays drawn, aiming, which is why the first arrow at a new
   * enemy also reads as a release.
   */
  private animateBow(state: TowerState, pos: Vec2): void {
    const draw = state.cooldown < BOW_DRAW_S ? 1 - state.cooldown / BOW_DRAW_S : 0;
    const released = this.bowDraw > 0.5 && draw === 0;
    this.bowDraw = draw;
    if (released) {
      this.bowReleasing = true;
      this.sprite.x = pos.x;
      this.scene.tweens.add({
        targets: this.sprite,
        scaleX: this.baseScale.x * 1.12,
        scaleY: this.baseScale.y * 0.94,
        duration: 70,
        yoyo: true,
        ease: 'Quad.easeOut',
        onComplete: () => {
          this.bowReleasing = false;
          if (this.sprite.active) this.sprite.setScale(this.baseScale.x, this.baseScale.y);
        },
      });
    }
    if (this.bowReleasing) return;
    this.sprite.setScale(
      this.baseScale.x * (1 - BOW_DRAW_SQUASH * draw),
      this.baseScale.y * (1 + BOW_DRAW_SQUASH * 0.5 * draw),
    );
    this.sprite.x = pos.x - BOW_DRAW_SHIFT * draw;
  }

  /**
   * Mirror a melee tower whose art has a facing, so it always bites the way it
   * is running.
   *
   * The direction comes from the unit's own displacement from its plot rather
   * than from the target's position: the target is not passed down here, and
   * the displacement is the same answer — the unit is always travelling
   * towards the enemy it has chosen. The flip is HELD through the return leg
   * and only cleared once the unit is home and idle, so a tower does not spin
   * on the spot between strikes.
   */
  private faceTarget(state: TowerState, drawPos: Vec2): void {
    const facing = SPRITE_FACING[state.type];
    if (facing === undefined || !state.melee) return;
    const dx = drawPos.x - state.pos.x;
    if (Math.abs(dx) <= FACE_DEADZONE) {
      if (state.melee.phase === 'idle') this.sprite.setFlipX(false);
      return;
    }
    // facing +1 art points right, so it mirrors when heading left, and vice versa.
    this.sprite.setFlipX(facing === 1 ? dx < 0 : dx > 0);
  }

  /** Sync position, ring, and retexture if the tower was upgraded. */
  update(state: TowerState, selected: boolean, frozen = false, fx?: TowerFx): void {
    // Melee units (the Cheese Bladesmen) roam to their target and back; follow
    // the sim's melee position. The range ring stays anchored at the base plot.
    const drawPos = state.melee ? state.melee.pos : state.pos;
    this.sprite.setPosition(drawPos.x, drawPos.y);
    this.faceTarget(state, drawPos);
    this.drawRing(state, selected);

    if (state.tier !== this.currentTier || state.type !== this.currentType) {
      this.sprite.setTexture(`tower-${state.type}-${state.tier}`);
      // Re-pin display size: setTexture resets the sprite to native texture size.
      const display = displaySizeFor(state.type);
      this.sprite.setDisplaySize(display.w, display.h);
      this.baseScale = { x: this.sprite.scaleX, y: this.sprite.scaleY };
      this.currentTier = state.tier;
      this.currentType = state.type;
      if (this.puddle && state.type === 'cheese') {
        const range = tierSpec('cheese', state.tier).range;
        this.puddle.setDisplaySize(range * 2, range * 2);
      }
    }

    if (state.type === 'bow') this.animateBow(state, drawPos);

    // Ember Barrage: a green bar over the tower showing structural HP. It only
    // exists once King Ember has armed the barrage, so it is built lazily and
    // then kept in sync. The fill stays green at every level — the bar SHRINKS
    // to show damage rather than changing colour.
    const hasHpBar = state.hp !== undefined && !!state.maxHp;
    if (hasHpBar) {
      const bx = state.pos.x - HP_BAR_W / 2;
      const by = state.pos.y + HP_BAR_OFFSET_Y;
      if (!this.hpBg) {
        this.hpBg = this.scene.add
          .rectangle(bx, by, HP_BAR_W, HP_BAR_H, HP_BG)
          .setOrigin(0, 0.5)
          .setDepth(3.6);
        this.hpBar = this.scene.add
          .rectangle(bx, by, HP_BAR_W, HP_BAR_H, HP_FILL)
          .setOrigin(0, 0.5)
          .setDepth(3.7);
      }
      this.hpBar?.setSize(HP_BAR_W * Math.max(0, state.hp! / state.maxHp!), HP_BAR_H);
    } else if (this.hpBg) {
      // The barrage is over (its last carrier is dead, or the arena was
      // cleared) and the sim has dropped this tower's hp. Without this branch
      // the bar simply stayed on screen for the rest of the run.
      this.hpBg.destroy();
      this.hpBar?.destroy();
      this.hpBg = undefined;
      this.hpBar = undefined;
    }

    // Shrekbook Hambi's caption normally sits just above the sprite — right
    // where the HP bar appears once King Ember arms the barrage, and at a higher
    // depth, so it hid the bar completely. Lift it clear while the bar exists.
    if (this.shout) {
      this.shout.setPosition(
        state.pos.x,
        state.pos.y + (hasHpBar ? SHOUT_OFFSET_Y_WITH_HP : SHOUT_OFFSET_Y),
      );
    }

    // After the caption has settled, so the potion icons can sit above it.
    this.updateFx(state, fx);

    // Tier-4 Ninja split: mirror the second melee copy with a second sprite.
    if (state.melee2) {
      if (!this.cloneSprite) {
        const display = displaySizeFor(state.type);
        this.cloneSprite = this.scene.add
          .sprite(state.melee2.pos.x, state.melee2.pos.y, `tower-${state.type}-${state.tier}`)
          .setDepth(3)
          .setDisplaySize(display.w, display.h)
          .setAlpha(0.92);
      }
      this.cloneSprite.setPosition(state.melee2.pos.x, state.melee2.pos.y);
      const facing = SPRITE_FACING[state.type];
      if (facing !== undefined) {
        const dx = state.melee2.pos.x - state.pos.x;
        if (Math.abs(dx) > FACE_DEADZONE) {
          this.cloneSprite.setFlipX(facing === 1 ? dx < 0 : dx > 0);
        }
      }
    } else if (this.cloneSprite) {
      this.cloneSprite.destroy();
      this.cloneSprite = undefined;
    }

    // Null-Hex stun: grey out the sprite and spin golden sparks above it.
    // Drawn with Graphics (not Text) so the overlap e2e's text sweep never
    // sees them. The orbit phase comes from the scene clock — purely visual.
    if ((state.stunnedFor ?? 0) > 0) {
      this.sprite.setTint(STUN_TINT);
      if (!this.stunGfx) this.stunGfx = this.scene.add.graphics().setDepth(3.8);
      const g = this.stunGfx;
      g.clear();
      const t = (this.scene.time.now / 1000) * STUN_ORBIT_SPEED;
      for (let i = 0; i < STUN_SPARKS; i++) {
        const a = t + (i * Math.PI * 2) / STUN_SPARKS;
        const sx = drawPos.x + Math.cos(a) * STUN_ORBIT_RX;
        const sy = drawPos.y + STUN_ORBIT_Y + Math.sin(a) * STUN_ORBIT_RY;
        g.fillStyle(0xf5c518, 0.95);
        g.fillCircle(sx, sy, 3.5);
        g.fillStyle(0xffffff, 0.9);
        g.fillCircle(sx, sy, 1.5);
      }
    } else {
      if (this.stunGfx) {
        this.stunGfx.destroy();
        this.stunGfx = undefined;
      }
      // Veggie Hex fire-rate slow: a green wash (no sparks — lighter than the
      // full stun). Stun above takes visual precedence when both apply.
      // A Poison Shroom's dose: a bright toxic green, over the hex's wash.
      if (state.poison) {
        this.sprite.setTint(POISON_TINT);
      } else if ((state.fireSlowFor ?? 0) > 0) {
        this.sprite.setTint(SLOW_TINT);
      } else {
        this.sprite.clearTint();
      }
    }

    // Ghosting: purple over everything else, the ghosts circling above.
    if (state.possessed) {
      const shroom = state.possessed.by === 'glowshroom';
      this.sprite.setTint(shroom ? SHROOMNITIZE_TINT : POSSESS_TINT);
      if (this.possessGhosts && this.possessBy !== state.possessed.by) {
        this.possessGhosts.forEach((g) => g.destroy());
        this.possessGhosts = undefined;
      }
      if (!this.possessGhosts) {
        this.possessBy = state.possessed.by;
        this.possessGhosts = Array.from({ length: POSSESS_GHOSTS }, () => {
          const img = this.scene.add
            .image(drawPos.x, drawPos.y, shroom ? 'enemy-glowshroom' : 'enemy-ghost')
            .setDisplaySize(POSSESS_GHOST_SIZE, POSSESS_GHOST_SIZE)
            .setDepth(3.85);
          return shroom ? img : img.setAlpha(0.8).setTint(0xd9b8ff);
        });
      }
      const t = (this.scene.time.now / 1000) * POSSESS_ORBIT_SPEED;
      this.possessGhosts.forEach((img, i) => {
        const a = t + (i * Math.PI * 2) / POSSESS_GHOSTS;
        img.setPosition(
          drawPos.x + Math.cos(a) * POSSESS_ORBIT_RX,
          drawPos.y + STUN_ORBIT_Y + Math.sin(a) * POSSESS_ORBIT_RY,
        );
        // The near half of the orbit draws over the tower, the far half under.
        img.setDepth(Math.sin(a) > 0 ? 3.85 : 3.75);
      });
    } else if (this.possessGhosts) {
      this.possessGhosts.forEach((g) => g.destroy());
      this.possessGhosts = undefined;
    }

    // Max-tier wizard: purple four-pointed stars orbiting the tower.
    if (state.type === 'wizard' && state.tier === 3) {
      if (!this.starGfx) this.starGfx = this.scene.add.graphics().setDepth(3.8);
      const g = this.starGfx;
      g.clear();
      const t = (this.scene.time.now / 1000) * WIZ_ORBIT_SPEED;
      for (let i = 0; i < WIZ_STARS; i++) {
        const a = t + (i * Math.PI * 2) / WIZ_STARS;
        const sx = drawPos.x + Math.cos(a) * WIZ_ORBIT_RX;
        const sy = drawPos.y + Math.sin(a) * WIZ_ORBIT_RY;
        g.fillStyle(WIZ_STAR_COLOR, 0.95);
        g.fillPoints(
          [
            { x: sx, y: sy - 5 },
            { x: sx + 2, y: sy },
            { x: sx, y: sy + 5 },
            { x: sx - 2, y: sy },
          ],
          true,
        );
        g.fillPoints(
          [
            { x: sx - 5, y: sy },
            { x: sx, y: sy - 2 },
            { x: sx + 5, y: sy },
            { x: sx, y: sy + 2 },
          ],
          true,
        );
        g.fillStyle(0xffffff, 0.9);
        g.fillCircle(sx, sy, 1.2);
      }
    }

    // Ninja Hambi (max tier) & Sensei Ketchup (tiers 2–3): a red aura glow and
    // orbiting red stones / stars — the visual signatures of their upgrades.
    // Drawn with Graphics (like the wizard stars) so the overlap e2e's text
    // sweep never sees them; orbit phase comes from the scene clock (visual).
    // Evolution recolors: ninja/sensei tier 4 glow green, the patty Evolution
    // gains a purple fiery aura; tier 3 keeps the classic red.
    const aura: [number, number] | null =
      (state.type === 'ninja' || state.type === 'sensei') && state.tier === 4
        ? AURA_GREEN
        : (state.type === 'ninja' || state.type === 'sensei') && state.tier === 3
          ? AURA_RED
          : state.type === 'patty' && state.tier === 4
            ? AURA_PURPLE
            : null;
    const stoneCount =
      state.type === 'sensei' ? (state.tier === 3 ? 4 : state.tier === 2 ? 3 : 0) : 0;
    const wantStars = state.type === 'sensei' && state.tier >= 3;
    // The tier-4 Sensei rings itself with green spikes (replacing the stones).
    const wantSpikes = state.type === 'sensei' && state.tier === 4;

    if (aura) {
      if (!this.auraGfx) this.auraGfx = this.scene.add.graphics().setDepth(2.7);
      const g = this.auraGfx;
      g.clear();
      const pulse = 0.5 + 0.5 * Math.sin((this.scene.time.now / 1000) * 3);
      const d = displaySizeFor(state.type);
      const r = Math.max(d.w, d.h) * 0.5 + 6 + pulse * 4;
      g.fillStyle(aura[0], 0.18);
      g.fillCircle(drawPos.x, drawPos.y + 6, r);
      g.fillStyle(aura[1], 0.14);
      g.fillCircle(drawPos.x, drawPos.y + 6, r * 0.7);
      // The split copy carries the same glow.
      if (state.melee2) {
        g.fillStyle(aura[0], 0.18);
        g.fillCircle(state.melee2.pos.x, state.melee2.pos.y + 6, r);
        g.fillStyle(aura[1], 0.14);
        g.fillCircle(state.melee2.pos.x, state.melee2.pos.y + 6, r * 0.7);
      }
    } else if (this.auraGfx) {
      this.auraGfx.destroy();
      this.auraGfx = undefined;
    }

    if (stoneCount > 0 || wantStars || wantSpikes) {
      if (!this.orbitGfx) this.orbitGfx = this.scene.add.graphics().setDepth(3.8);
      const g = this.orbitGfx;
      g.clear();
      const t = (this.scene.time.now / 1000) * 1.6;
      for (let i = 0; i < stoneCount; i++) {
        const a = t + (i * Math.PI * 2) / stoneCount;
        const sx = drawPos.x + Math.cos(a) * 30;
        const sy = drawPos.y + Math.sin(a) * 11 - 4;
        g.fillStyle(0x8a1f16, 1); // dark red gemstone
        g.fillCircle(sx, sy, 4);
        g.fillStyle(0xe0483a, 0.9); // lit facet
        g.fillCircle(sx - 1, sy - 1, 1.6);
      }
      if (wantSpikes) {
        // A slow ring of eight outward green spikes — the Evolution's thorns.
        const ta = (this.scene.time.now / 1000) * 0.5;
        for (let i = 0; i < 8; i++) {
          const a = ta + (i * Math.PI * 2) / 8;
          const bx = drawPos.x + Math.cos(a) * 44;
          const by = drawPos.y + Math.sin(a) * 17;
          const tipX = drawPos.x + Math.cos(a) * 54;
          const tipY = drawPos.y + Math.sin(a) * 21;
          const px = Math.cos(a + Math.PI / 2) * 3;
          const py = Math.sin(a + Math.PI / 2) * 3;
          g.fillStyle(0x1fa83c, 0.9);
          g.fillPoints(
            [
              { x: bx + px, y: by + py },
              { x: tipX, y: tipY },
              { x: bx - px, y: by - py },
            ],
            true,
          );
        }
      }
      if (wantStars) {
        // 4 red stars at tier 3; the Evolution counter-rotates SIX green ones.
        const starCount = state.tier === 4 ? 6 : 4;
        const starColor = state.tier === 4 ? 0x2ee06a : 0xff4d3d;
        const centerColor = state.tier === 4 ? 0xd8ffe0 : 0xffe08a;
        const ts = -(this.scene.time.now / 1000) * 1.8; // counter-rotating
        for (let i = 0; i < starCount; i++) {
          const a = ts + (i * Math.PI * 2) / starCount;
          const sx = drawPos.x + Math.cos(a) * 38;
          const sy = drawPos.y + Math.sin(a) * 14;
          g.fillStyle(starColor, 0.95);
          g.fillPoints(
            [
              { x: sx, y: sy - 5 },
              { x: sx + 2, y: sy },
              { x: sx, y: sy + 5 },
              { x: sx - 2, y: sy },
            ],
            true,
          );
          g.fillPoints(
            [
              { x: sx - 5, y: sy },
              { x: sx, y: sy - 2 },
              { x: sx + 5, y: sy },
              { x: sx, y: sy + 2 },
            ],
            true,
          );
          g.fillStyle(centerColor, 0.9);
          g.fillCircle(sx, sy, 1.2);
        }
      }
    } else if (this.orbitGfx) {
      this.orbitGfx.destroy();
      this.orbitGfx = undefined;
    }

    // Angelic Hambi (spec 2026-10-13): golden stars circle him — 3, then 4,
    // then 5 by tier — on a tilted orbit, in front of him on the near half and
    // behind him on the far half. Purely visual, on the scene clock.
    this.updateAngelStars(state, drawPos);

    // The bar comes and goes with the charge: a Phantom upgraded to tier 3 earns
    // its red bar here, the frame the special appears in its spec.
    if (!this.wantsCdBar(state)) {
      if (this.cdBg) {
        this.cdBg.destroy();
        this.cdBar?.destroy();
        this.cdBg = undefined;
        this.cdBar = undefined;
      }
    } else {
      if (!this.cdBg) this.buildCdBar(state);
      const spec = tierSpec(state.type, state.tier);
      // wise: drains over the freeze cooldown (bright full bar during the freeze);
      // wizard: drains over the 15 s green-shot charge (ignores the map freeze);
      // phantom: the same `special.interval` path as the wizard — its 30 s
      // hitscan charge — differing only in the red fill picked at construction.
      const isWise = state.type === 'wise';
      const max = (isWise ? spec.abilityCooldown : spec.special?.interval) ?? 1;
      const remaining = (isWise ? state.abilityCooldown : state.specialCooldown) ?? max;
      const active = isWise && frozen;
      const ratio = active ? 1 : Math.max(0, Math.min(1, remaining / max));
      const bx = state.pos.x - CD_BAR_W / 2;
      const by = state.pos.y + CD_BAR_OFFSET_Y;
      this.cdBg!.setPosition(bx, by);
      this.cdBar!.setPosition(bx, by);
      this.cdBar!.setSize(CD_BAR_W * ratio, CD_BAR_H);
      this.cdBar!.setFillStyle(active ? CD_FILL_ACTIVE : this.cdFill);
    }
  }

  private updateAngelStars(state: TowerState, drawPos: Vec2): void {
    const want =
      state.type === 'angelic' && this.scene.textures.exists('fx-angelstar')
        ? Math.min(5, state.tier + 2)
        : 0;
    while (this.angelStars.length > want) this.angelStars.pop()?.destroy();
    while (this.angelStars.length < want) {
      this.angelStars.push(
        this.scene.add
          .image(drawPos.x, drawPos.y, 'fx-angelstar')
          .setDisplaySize(16, 16)
          .setName('angelic-star'),
      );
    }
    const t = (this.scene.time.now / 1000) * 1.4;
    this.angelStars.forEach((star, i) => {
      const a = t + (i * Math.PI * 2) / this.angelStars.length;
      const front = Math.sin(a) > 0;
      star
        .setPosition(drawPos.x + Math.cos(a) * 40, drawPos.y + Math.sin(a) * 15 - 2)
        .setDepth(front ? 3.8 : 2.9)
        .setAngle(a * 40);
    });
  }

  /**
   * Burn away: fade every object this view owns to nothing over FADE_MS, then
   * destroy it. Used when a tower leaves the sim — King Ember's fireball taking
   * its last HP, or the player selling it — so the plot empties with a visible
   * beat instead of the sprite vanishing between frames.
   *
   * `onDone` fires after the fade so the owner can drop its reference.
   */
  fadeOutAndDestroy(onDone: () => void): void {
    const targets = [
      this.ring,
      this.sprite,
      this.puddle,
      this.cloneSprite,
      this.cdBg,
      this.cdBar,
      this.stunGfx,
      this.starGfx,
      this.auraGfx,
      this.orbitGfx,
      this.hpBg,
      this.hpBar,
      this.shout,
      this.shieldStar,
      ...this.fxIcons,
      ...this.angelStars,
    ].filter((o): o is NonNullable<typeof o> => o != null);
    this.scene.tweens.add({
      targets,
      alpha: 0,
      duration: FADE_MS,
      ease: 'Quad.easeIn',
      onComplete: () => {
        this.destroy();
        onDone();
      },
    });
  }

  /** Potion icons in a row above the tower; the blue star above those. */
  private updateFx(state: TowerState, fx: TowerFx | undefined): void {
    // Shrekbook Hambi's caption ("SHREKBOOK!") sits where the icons normally
    // go, so on a captioned tower the row lifts clear above the caption.
    const iconY = this.shout
      ? this.shout.y - this.shout.displayHeight - FX_ICON_SIZE / 2 - 2
      : state.pos.y + FX_ICON_OFFSET_Y;
    const keys = `${fx ? fx.icons.join(',') : ''}@${Math.round(iconY)}`;
    if (keys !== this.fxKeys) {
      this.fxKeys = keys;
      this.fxIcons.forEach((i) => i.destroy());
      this.fxIcons = [];
      const list = fx ? fx.icons.filter((k) => this.scene.textures.exists(k)) : [];
      const span = list.length * FX_ICON_SIZE + Math.max(0, list.length - 1) * FX_ICON_GAP;
      list.forEach((key, i) => {
        const x = state.pos.x - span / 2 + FX_ICON_SIZE / 2 + i * (FX_ICON_SIZE + FX_ICON_GAP);
        this.fxIcons.push(
          this.scene.add
            .image(x, iconY, key)
            .setDisplaySize(FX_ICON_SIZE, FX_ICON_SIZE)
            .setDepth(3.85),
        );
      });
    }
    const shield = !!fx?.shield && this.scene.textures.exists('icon-bluestar');
    const starY = this.shout
      ? iconY - FX_ICON_SIZE / 2 - SHIELD_STAR_SIZE / 2 - 2
      : state.pos.y + SHIELD_STAR_OFFSET_Y;
    if (shield && !this.shieldStar) {
      this.shieldStar = this.scene.add
        .image(state.pos.x, starY, 'icon-bluestar')
        .setDisplaySize(SHIELD_STAR_SIZE, SHIELD_STAR_SIZE)
        .setDepth(3.9);
    } else if (!shield && this.shieldStar) {
      this.shieldStar.destroy();
      this.shieldStar = undefined;
    }
    if (this.shieldStar) {
      // A gentle float, on the scene clock — purely visual.
      const bob = Math.sin(this.scene.time.now / 400) * 4;
      this.shieldStar.setPosition(state.pos.x, starY + bob);
    }
  }

  /** Remove all Phaser game objects owned by this view. */
  destroy(): void {
    this.scene.tweens.killTweensOf(this.sprite);
    this.fxIcons.forEach((i) => i.destroy());
    this.fxIcons = [];
    this.shieldStar?.destroy();
    this.shieldStar = undefined;
    this.ring.destroy();
    this.hpBg?.destroy();
    this.hpBar?.destroy();
    this.puddle?.destroy();
    this.cloneSprite?.destroy();
    this.cdBg?.destroy();
    this.cdBar?.destroy();
    this.stunGfx?.destroy();
    this.possessGhosts?.forEach((g) => g.destroy());
    this.starGfx?.destroy();
    this.auraGfx?.destroy();
    this.orbitGfx?.destroy();
    this.angelStars.forEach((s) => s.destroy());
    this.angelStars = [];
    this.shout?.destroy();
    this.sprite.destroy();
  }
}
